
import { Course } from './types';

export const TYPESCRIPT_REACT_COURSE: Course = {
  id: "typescript-react",
  title: "TypeScript + React Professional",
  description: "Master type-safe React development with TypeScript for professional applications",
  level: "intermediate",
  duration: "12 hours",
  totalDuration: "12 hours",
  estimatedHours: 12,
  badge: "Professional Course",
  modules: [
    {
      id: "ts-react-1",
      title: "TypeScript Fundamentals for React",
      description: "Learn TypeScript basics and how they apply to React development",
      duration: "90 minutes",
      estimatedTime: 90,
      difficulty: 'intermediate',
      prerequisites: ['basic-8'],
      learningObjectives: [
        "Understand TypeScript basic types and syntax",
        "Configure TypeScript for React projects",
        "Use TypeScript with React components",
        "Debug TypeScript errors effectively"
      ],
      content: `
        <h1>TypeScript Fundamentals for React</h1>
        
        <h2>What is TypeScript?</h2>
        <p>TypeScript is a statically typed superset of JavaScript that compiles to plain JavaScript. It adds optional static type checking to JavaScript, helping catch errors at compile time rather than runtime.</p>
        
        <h2>Why TypeScript with React?</h2>
        <ul>
          <li><strong>Type Safety:</strong> Catch errors before they reach production</li>
          <li><strong>Better IDE Support:</strong> Enhanced autocomplete and refactoring</li>
          <li><strong>Self-Documenting Code:</strong> Types serve as inline documentation</li>
          <li><strong>Easier Refactoring:</strong> Confident code changes with type checking</li>
        </ul>
        
        <h2>Setting Up TypeScript with React</h2>
        <pre><code>npx create-react-app my-app --template typescript</code></pre>
        
        <h2>Basic TypeScript Types</h2>
        <pre><code>// Primitive types
let name: string = "John";
let age: number = 30;
let isActive: boolean = true;

// Arrays
let numbers: number[] = [1, 2, 3];
let names: Array<string> = ["Alice", "Bob"];

// Objects
interface User {
  id: number;
  name: string;
  email: string;
}

let user: User = {
  id: 1,
  name: "John Doe",
  email: "john@example.com"
};</code></pre>
        
        <h2>TypeScript with React Components</h2>
        <pre><code>import React from 'react';

// Function component with TypeScript
interface WelcomeProps {
  name: string;
  age?: number; // Optional prop
}

const Welcome: React.FC<WelcomeProps> = ({ name, age }) => {
  return (
    <div>
      <h1>Hello, {name}!</h1>
      {age && <p>You are {age} years old.</p>}
    </div>
  );
};

export default Welcome;</code></pre>
      `,
      practice: {
        task: "Convert a JavaScript React component to TypeScript with proper type definitions",
        starter: `// Convert this JavaScript component to TypeScript
import React, { useState } from 'react';

const UserProfile = ({ user, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  
  return (
    <div>
      <h2>{user.name}</h2>
      <p>{user.email}</p>
      <button onClick={() => setIsEditing(!isEditing)}>
        {isEditing ? 'Save' : 'Edit'}
      </button>
    </div>
  );
};`,
        solution: `import React, { useState } from 'react';

interface User {
  id: number;
  name: string;
  email: string;
}

interface UserProfileProps {
  user: User;
  onUpdate: (user: User) => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ user, onUpdate }) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  
  return (
    <div>
      <h2>{user.name}</h2>
      <p>{user.email}</p>
      <button onClick={() => setIsEditing(!isEditing)}>
        {isEditing ? 'Save' : 'Edit'}
      </button>
    </div>
  );
};

export default UserProfile;`,
        hints: [
          "Define interfaces for complex objects like User",
          "Use React.FC<PropsType> for functional components",
          "Add type parameters to useState for state typing"
        ]
      }
    },
    {
      id: "ts-react-2",
      title: "Advanced Component Typing",
      description: "Master advanced TypeScript patterns for React components",
      duration: "100 minutes",
      estimatedTime: 100,
      difficulty: 'intermediate',
      prerequisites: ['ts-react-1'],
      learningObjectives: [
        "Use generic components effectively",
        "Type children and render props",
        "Handle events with proper typing",
        "Create reusable component patterns"
      ],
      content: `
        <h1>Advanced Component Typing</h1>
        
        <h2>Generic Components</h2>
        <p>Create reusable components that work with different data types:</p>
        <pre><code>interface ListProps<T> {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
}

function List<T>({ items, renderItem }: ListProps<T>) {
  return (
    <ul>
      {items.map((item, index) => (
        <li key={index}>{renderItem(item)}</li>
      ))}
    </ul>
  );
}

// Usage
const users = [{ name: "John" }, { name: "Jane" }];
<List items={users} renderItem={user => <span>{user.name}</span>} /></code></pre>
        
        <h2>Event Handling</h2>
        <pre><code>import React from 'react';

interface FormProps {
  onSubmit: (data: { name: string; email: string }) => void;
}

const Form: React.FC<FormProps> = ({ onSubmit }) => {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    onSubmit({
      name: formData.get('name') as string,
      email: formData.get('email') as string
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log(e.target.value);
  };

  return (
    <form onSubmit={handleSubmit}>
      <input name="name" onChange={handleChange} />
      <input name="email" type="email" />
      <button type="submit">Submit</button>
    </form>
  );
};</code></pre>
        
        <h2>Children and Render Props</h2>
        <pre><code>// Typing children
interface ContainerProps {
  children: React.ReactNode;
  className?: string;
}

const Container: React.FC<ContainerProps> = ({ children, className }) => (
  <div className={className}>{children}</div>
);

// Render props pattern
interface DataFetcherProps<T> {
  url: string;
  children: (data: T | null, loading: boolean, error: string | null) => React.ReactNode;
}

function DataFetcher<T>({ url, children }: DataFetcherProps<T>) {
  // Implementation here
  return <>{children(data, loading, error)}</>;
}</code></pre>
      `,
      practice: {
        task: "Create a generic Modal component with proper TypeScript typing",
        starter: `// Create a reusable Modal component with TypeScript`,
        solution: `import React from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
}

const Modal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'medium' 
}) => {
  if (!isOpen) return null;

  const sizeClasses = {
    small: 'max-w-md',
    medium: 'max-w-lg',
    large: 'max-w-2xl'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className={\`bg-white rounded-lg p-6 \${sizeClasses[size]}\`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
};

export default Modal;`,
        hints: [
          "Use union types for size variants",
          "Make optional props with default values",
          "Type children as React.ReactNode"
        ]
      }
    },
    {
      id: "ts-react-3",
      title: "Hooks with TypeScript",
      description: "Learn to use React hooks with proper TypeScript typing",
      duration: "110 minutes",
      estimatedTime: 110,
      difficulty: 'intermediate',
      prerequisites: ['ts-react-2'],
      learningObjectives: [
        "Type useState and useEffect hooks",
        "Create custom hooks with TypeScript",
        "Handle async operations with proper typing",
        "Use useReducer with TypeScript"
      ],
      content: `
        <h1>Hooks with TypeScript</h1>
        
        <h2>useState with TypeScript</h2>
        <pre><code>import React, { useState } from 'react';

// Simple state
const [count, setCount] = useState<number>(0);

// Complex state
interface User {
  id: number;
  name: string;
  email: string;
}

const [user, setUser] = useState<User | null>(null);

// Array state
const [items, setItems] = useState<string[]>([]);

// Object state
interface FormState {
  name: string;
  email: string;
  age: number;
}

const [form, setForm] = useState<FormState>({
  name: '',
  email: '',
  age: 0
});</code></pre>
        
        <h2>useEffect with TypeScript</h2>
        <pre><code>import React, { useEffect, useState } from 'react';

interface ApiResponse {
  data: User[];
  total: number;
}

const UserList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async (): Promise<void> => {
      try {
        const response = await fetch('/api/users');
        const data: ApiResponse = await response.json();
        setUsers(data.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <ul>
      {users.map(user => (
        <li key={user.id}>{user.name}</li>
      ))}
    </ul>
  );
};</code></pre>
        
        <h2>Custom Hooks with TypeScript</h2>
        <pre><code>import { useState, useEffect } from 'react';

// Custom hook return type
interface UseFetchResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function useFetch<T>(url: string): UseFetchResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await fetch(url);
      const result: T = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [url]);

  return { data, loading, error, refetch: fetchData };
}

// Usage
const UserProfile: React.FC<{ userId: number }> = ({ userId }) => {
  const { data: user, loading, error } = useFetch<User>(\`/api/users/\${userId}\`);
  
  // Component implementation
};</code></pre>
        
        <h2>useReducer with TypeScript</h2>
        <pre><code>import React, { useReducer } from 'react';

// State type
interface CounterState {
  count: number;
  history: number[];
}

// Action types
type CounterAction = 
  | { type: 'INCREMENT' }
  | { type: 'DECREMENT' }
  | { type: 'RESET' }
  | { type: 'SET'; payload: number };

// Reducer function
const counterReducer = (state: CounterState, action: CounterAction): CounterState => {
  switch (action.type) {
    case 'INCREMENT':
      return {
        count: state.count + 1,
        history: [...state.history, state.count + 1]
      };
    case 'DECREMENT':
      return {
        count: state.count - 1,
        history: [...state.history, state.count - 1]
      };
    case 'RESET':
      return {
        count: 0,
        history: [0]
      };
    case 'SET':
      return {
        count: action.payload,
        history: [...state.history, action.payload]
      };
    default:
      return state;
  }
};

// Component using useReducer
const Counter: React.FC = () => {
  const [state, dispatch] = useReducer(counterReducer, {
    count: 0,
    history: [0]
  });

  return (
    <div>
      <p>Count: {state.count}</p>
      <button onClick={() => dispatch({ type: 'INCREMENT' })}>+</button>
      <button onClick={() => dispatch({ type: 'DECREMENT' })}>-</button>
      <button onClick={() => dispatch({ type: 'RESET' })}>Reset</button>
    </div>
  );
};</code></pre>
      `,
      practice: {
        task: "Create a custom hook for managing form state with TypeScript",
        starter: `// Create useForm hook with proper TypeScript typing`,
        solution: `import { useState, ChangeEvent } from 'react';

interface UseFormResult<T> {
  values: T;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (callback: (values: T) => void) => (e: React.FormEvent) => void;
  reset: () => void;
  setValue: (field: keyof T, value: T[keyof T]) => void;
}

function useForm<T extends Record<string, any>>(initialValues: T): UseFormResult<T> {
  const [values, setValues] = useState<T>(initialValues);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (callback: (values: T) => void) => (e: React.FormEvent) => {
    e.preventDefault();
    callback(values);
  };

  const reset = () => setValues(initialValues);

  const setValue = (field: keyof T, value: T[keyof T]) => {
    setValues(prev => ({ ...prev, [field]: value }));
  };

  return { values, handleChange, handleSubmit, reset, setValue };
}

export default useForm;`,
        hints: [
          "Use generic constraints with Record<string, any>",
          "Type event handlers properly",
          "Return an object with all necessary methods"
        ]
      }
    },
    {
      id: "ts-react-4",
      title: "State Management with TypeScript",
      description: "Implement type-safe state management patterns",
      duration: "120 minutes",
      estimatedTime: 120,
      difficulty: 'advanced',
      prerequisites: ['ts-react-3'],
      learningObjectives: [
        "Implement Context API with TypeScript",
        "Create type-safe Redux patterns",
        "Use Zustand with TypeScript",
        "Handle complex state structures"
      ],
      content: `
        <h1>State Management with TypeScript</h1>
        
        <h2>Context API with TypeScript</h2>
        <pre><code>import React, { createContext, useContext, useReducer } from 'react';

// User state types
interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'user';
}

interface UserState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

// Action types
type UserAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOGOUT' };

// Context type
interface UserContextType {
  state: UserState;
  dispatch: React.Dispatch<UserAction>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

// Create context with default value
const UserContext = createContext<UserContextType | undefined>(undefined);

// Reducer
const userReducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload, loading: false, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'LOGOUT':
      return { ...state, user: null, error: null };
    default:
      return state;
  }
};

// Provider component
interface UserProviderProps {
  children: React.ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, {
    user: null,
    loading: false,
    error: null
  });

  const login = async (email: string, password: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const user: User = await response.json();
      dispatch({ type: 'SET_USER', payload: user });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Login failed' });
    }
  };

  const logout = (): void => {
    dispatch({ type: 'LOGOUT' });
  };

  return (
    <UserContext.Provider value={{ state, dispatch, login, logout }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};</code></pre>
        
        <h2>Zustand with TypeScript</h2>
        <pre><code>import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface Todo {
  id: number;
  text: string;
  completed: boolean;
}

interface TodoState {
  todos: Todo[];
  addTodo: (text: string) => void;
  toggleTodo: (id: number) => void;
  removeTodo: (id: number) => void;
  clearCompleted: () => void;
}

export const useTodoStore = create<TodoState>()(
  devtools(
    (set, get) => ({
      todos: [],
      addTodo: (text: string) =>
        set((state) => ({
          todos: [
            ...state.todos,
            { id: Date.now(), text, completed: false }
          ]
        })),
      toggleTodo: (id: number) =>
        set((state) => ({
          todos: state.todos.map((todo) =>
            todo.id === id ? { ...todo, completed: !todo.completed } : todo
          )
        })),
      removeTodo: (id: number) =>
        set((state) => ({
          todos: state.todos.filter((todo) => todo.id !== id)
        })),
      clearCompleted: () =>
        set((state) => ({
          todos: state.todos.filter((todo) => !todo.completed)
        }))
    }),
    { name: 'todo-store' }
  )
);

// Usage in component
const TodoList: React.FC = () => {
  const { todos, addTodo, toggleTodo, removeTodo } = useTodoStore();
  
  return (
    <div>
      {todos.map(todo => (
        <div key={todo.id}>
          <input
            type="checkbox"
            checked={todo.completed}
            onChange={() => toggleTodo(todo.id)}
          />
          <span>{todo.text}</span>
          <button onClick={() => removeTodo(todo.id)}>Delete</button>
        </div>
      ))}
    </div>
  );
};</code></pre>
      `,
      practice: {
        task: "Create a shopping cart context with TypeScript for an e-commerce app",
        starter: `// Create a CartContext with add, remove, and update quantity functionality`,
        solution: `import React, { createContext, useContext, useReducer } from 'react';

interface Product {
  id: number;
  name: string;
  price: number;
  image: string;
}

interface CartItem extends Product {
  quantity: number;
}

interface CartState {
  items: CartItem[];
  total: number;
  itemCount: number;
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: Product }
  | { type: 'REMOVE_ITEM'; payload: number }
  | { type: 'UPDATE_QUANTITY'; payload: { id: number; quantity: number } }
  | { type: 'CLEAR_CART' };

interface CartContextType {
  state: CartState;
  addItem: (product: Product) => void;
  removeItem: (id: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItem = state.items.find(item => item.id === action.payload.id);
      const newItems = existingItem
        ? state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        : [...state.items, { ...action.payload, quantity: 1 }];
      
      return {
        items: newItems,
        total: newItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
        itemCount: newItems.reduce((sum, item) => sum + item.quantity, 0)
      };
    }
    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(item => item.id !== action.payload);
      return {
        items: newItems,
        total: newItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
        itemCount: newItems.reduce((sum, item) => sum + item.quantity, 0)
      };
    }
    case 'UPDATE_QUANTITY': {
      const newItems = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, quantity: action.payload.quantity }
          : item
      ).filter(item => item.quantity > 0);
      
      return {
        items: newItems,
        total: newItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
        itemCount: newItems.reduce((sum, item) => sum + item.quantity, 0)
      };
    }
    case 'CLEAR_CART':
      return { items: [], total: 0, itemCount: 0 };
    default:
      return state;
  }
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    total: 0,
    itemCount: 0
  });

  const addItem = (product: Product) => {
    dispatch({ type: 'ADD_ITEM', payload: product });
  };

  const removeItem = (id: number) => {
    dispatch({ type: 'REMOVE_ITEM', payload: id });
  };

  const updateQuantity = (id: number, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  return (
    <CartContext.Provider value={{ state, addItem, removeItem, updateQuantity, clearCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};`,
        hints: [
          "Define clear interfaces for products and cart items",
          "Calculate totals in the reducer",
          "Handle quantity updates and removals properly"
        ]
      }
    },
    {
      id: "ts-react-5",
      title: "Testing TypeScript React Components",
      description: "Learn to test TypeScript React components effectively",
      duration: "100 minutes",
      estimatedTime: 100,
      difficulty: 'advanced',
      prerequisites: ['ts-react-4'],
      learningObjectives: [
        "Set up testing environment for TypeScript",
        "Write type-safe tests",
        "Mock TypeScript modules and functions",
        "Test custom hooks with TypeScript"
      ],
      content: `
        <h1>Testing TypeScript React Components</h1>
        
        <h2>Setting Up Testing Environment</h2>
        <pre><code>// jest.config.js
module.exports = {
  preset: 'ts-jest',
  testEnvironment: 'jsdom',
  setupFilesAfterEnv: ['<rootDir>/src/setupTests.ts'],
  moduleNameMapping: {
    '^@/(.*)$': '<rootDir>/src/$1'
  },
  transform: {
    '^.+\\.tsx?$': 'ts-jest'
  }
};

// src/setupTests.ts
import '@testing-library/jest-dom';</code></pre>
        
        <h2>Testing Components with Props</h2>
        <pre><code>import React from 'react';
import { render, screen, fireEvent } from '@testing-library/react';
import '@testing-library/jest-dom';
import Button from '../Button';

// Component to test
interface ButtonProps {
  variant: 'primary' | 'secondary';
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}

const Button: React.FC<ButtonProps> = ({ variant, onClick, children, disabled = false }) => {
  return (
    <button
      className={\`btn btn-\${variant}\`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

// Tests
describe('Button Component', () => {
  const mockOnClick = jest.fn();

  beforeEach(() => {
    mockOnClick.mockClear();
  });

  it('renders with correct text', () => {
    render(
      <Button variant="primary" onClick={mockOnClick}>
        Click me
      </Button>
    );
    
    expect(screen.getByText('Click me')).toBeInTheDocument();
  });

  it('applies correct variant class', () => {
    render(
      <Button variant="secondary" onClick={mockOnClick}>
        Test
      </Button>
    );
    
    const button = screen.getByRole('button');
    expect(button).toHaveClass('btn-secondary');
  });

  it('calls onClick when clicked', () => {
    render(
      <Button variant="primary" onClick={mockOnClick}>
        Click me
      </Button>
    );
    
    fireEvent.click(screen.getByRole('button'));
    expect(mockOnClick).toHaveBeenCalledTimes(1);
  });

  it('is disabled when disabled prop is true', () => {
    render(
      <Button variant="primary" onClick={mockOnClick} disabled>
        Disabled
      </Button>
    );
    
    const button = screen.getByRole('button');
    expect(button).toBeDisabled();
  });
});</code></pre>
        
        <h2>Testing Custom Hooks</h2>
        <pre><code>import { renderHook, act } from '@testing-library/react';
import { useCounter } from '../useCounter';

// Hook to test
interface UseCounterResult {
  count: number;
  increment: () => void;
  decrement: () => void;
  reset: () => void;
}

const useCounter = (initialValue: number = 0): UseCounterResult => {
  const [count, setCount] = useState(initialValue);

  const increment = useCallback(() => {
    setCount(prev => prev + 1);
  }, []);

  const decrement = useCallback(() => {
    setCount(prev => prev - 1);
  }, []);

  const reset = useCallback(() => {
    setCount(initialValue);
  }, [initialValue]);

  return { count, increment, decrement, reset };
};

// Tests
describe('useCounter Hook', () => {
  it('initializes with default value', () => {
    const { result } = renderHook(() => useCounter());
    expect(result.current.count).toBe(0);
  });

  it('initializes with custom value', () => {
    const { result } = renderHook(() => useCounter(10));
    expect(result.current.count).toBe(10);
  });

  it('increments count', () => {
    const { result } = renderHook(() => useCounter());
    
    act(() => {
      result.current.increment();
    });
    
    expect(result.current.count).toBe(1);
  });

  it('decrements count', () => {
    const { result } = renderHook(() => useCounter(5));
    
    act(() => {
      result.current.decrement();
    });
    
    expect(result.current.count).toBe(4);
  });

  it('resets to initial value', () => {
    const { result } = renderHook(() => useCounter(10));
    
    act(() => {
      result.current.increment();
      result.current.increment();
    });
    
    expect(result.current.count).toBe(12);
    
    act(() => {
      result.current.reset();
    });
    
    expect(result.current.count).toBe(10);
  });
});</code></pre>
        
        <h2>Mocking with TypeScript</h2>
        <pre><code>import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';
import { UserProfile } from '../UserProfile';
import * as userService from '../services/userService';

// Mock the service
jest.mock('../services/userService');
const mockedUserService = userService as jest.Mocked<typeof userService>;

// Component that uses the service
interface User {
  id: number;
  name: string;
  email: string;
}

const UserProfile: React.FC<{ userId: number }> = ({ userId }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    userService.getUser(userId)
      .then(setUser)
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div>Loading...</div>;
  if (!user) return <div>User not found</div>;

  return (
    <div>
      <h1>{user.name}</h1>
      <p>{user.email}</p>
    </div>
  );
};

// Tests
describe('UserProfile Component', () => {
  beforeEach(() => {
    jest.clearAllMocks();
  });

  it('displays user information when loaded', async () => {
    const mockUser: User = {
      id: 1,
      name: 'John Doe',
      email: 'john@example.com'
    };

    mockedUserService.getUser.mockResolvedValue(mockUser);

    render(<UserProfile userId={1} />);

    expect(screen.getByText('Loading...')).toBeInTheDocument();

    await waitFor(() => {
      expect(screen.getByText('John Doe')).toBeInTheDocument();
      expect(screen.getByText('john@example.com')).toBeInTheDocument();
    });

    expect(mockedUserService.getUser).toHaveBeenCalledWith(1);
  });

  it('displays error message when user not found', async () => {
    mockedUserService.getUser.mockRejectedValue(new Error('User not found'));

    render(<UserProfile userId={999} />);

    await waitFor(() => {
      expect(screen.getByText('User not found')).toBeInTheDocument();
    });
  });
});</code></pre>
      `,
      practice: {
        task: "Write comprehensive tests for a TypeScript React form component",
        starter: `// Test the following form component with TypeScript`,
        solution: `import React from 'react';
import { render, screen, fireEvent, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';
import '@testing-library/jest-dom';
import ContactForm from '../ContactForm';

// Mock submission function
const mockOnSubmit = jest.fn();

describe('ContactForm Component', () => {
  beforeEach(() => {
    mockOnSubmit.mockClear();
  });

  it('renders all form fields', () => {
    render(<ContactForm onSubmit={mockOnSubmit} />);
    
    expect(screen.getByLabelText(/name/i)).toBeInTheDocument();
    expect(screen.getByLabelText(/email/i)).toBeInTheDocument();
    expect(screen.getByLabelText(/message/i)).toBeInTheDocument();
    expect(screen.getByRole('button', { name: /submit/i })).toBeInTheDocument();
  });

  it('validates required fields', async () => {
    const user = userEvent.setup();
    render(<ContactForm onSubmit={mockOnSubmit} />);
    
    const submitButton = screen.getByRole('button', { name: /submit/i });
    await user.click(submitButton);
    
    expect(screen.getByText(/name is required/i)).toBeInTheDocument();
    expect(screen.getByText(/email is required/i)).toBeInTheDocument();
    expect(mockOnSubmit).not.toHaveBeenCalled();
  });

  it('validates email format', async () => {
    const user = userEvent.setup();
    render(<ContactForm onSubmit={mockOnSubmit} />);
    
    const emailInput = screen.getByLabelText(/email/i);
    await user.type(emailInput, 'invalid-email');
    
    const submitButton = screen.getByRole('button', { name: /submit/i });
    await user.click(submitButton);
    
    expect(screen.getByText(/invalid email format/i)).toBeInTheDocument();
    expect(mockOnSubmit).not.toHaveBeenCalled();
  });

  it('submits form with valid data', async () => {
    const user = userEvent.setup();
    render(<ContactForm onSubmit={mockOnSubmit} />);
    
    await user.type(screen.getByLabelText(/name/i), 'John Doe');
    await user.type(screen.getByLabelText(/email/i), 'john@example.com');
    await user.type(screen.getByLabelText(/message/i), 'Hello, this is a test message');
    
    const submitButton = screen.getByRole('button', { name: /submit/i });
    await user.click(submitButton);
    
    await waitFor(() => {
      expect(mockOnSubmit).toHaveBeenCalledWith({
        name: 'John Doe',
        email: 'john@example.com',
        message: 'Hello, this is a test message'
      });
    });
  });

  it('disables submit button while submitting', async () => {
    const user = userEvent.setup();
    const slowSubmit = jest.fn(() => new Promise(resolve => setTimeout(resolve, 100)));
    
    render(<ContactForm onSubmit={slowSubmit} />);
    
    await user.type(screen.getByLabelText(/name/i), 'John Doe');
    await user.type(screen.getByLabelText(/email/i), 'john@example.com');
    
    const submitButton = screen.getByRole('button', { name: /submit/i });
    await user.click(submitButton);
    
    expect(submitButton).toBeDisabled();
    expect(screen.getByText(/submitting.../i)).toBeInTheDocument();
    
    await waitFor(() => {
      expect(submitButton).toBeEnabled();
    });
  });
});`,
        hints: [
          "Use userEvent for better interaction simulation",
          "Test both validation and successful submission",
          "Mock async operations and test loading states"
        ]
      }
    },
    {
      id: "ts-react-6",
      title: "Production TypeScript React",
      description: "Deploy and maintain TypeScript React applications in production",
      duration: "110 minutes",
      estimatedTime: 110,
      difficulty: 'advanced',
      prerequisites: ['ts-react-5'],
      learningObjectives: [
        "Configure build tools for production",
        "Implement proper error boundaries",
        "Optimize TypeScript build performance",
        "Handle environment-specific configurations"
      ],
      content: `
        <h1>Production TypeScript React</h1>
        
        <h2>Build Configuration</h2>
        <pre><code>// tsconfig.json for production
{
  "compilerOptions": {
    "target": "ES2020",
    "lib": ["DOM", "DOM.Iterable", "ES6"],
    "allowJs": true,
    "skipLibCheck": true,
    "esModuleInterop": true,
    "allowSyntheticDefaultImports": true,
    "strict": true,
    "forceConsistentCasingInFileNames": true,
    "noFallthroughCasesInSwitch": true,
    "module": "esnext",
    "moduleResolution": "node",
    "resolveJsonModule": true,
    "isolatedModules": true,
    "noEmit": true,
    "jsx": "react-jsx",
    "incremental": true,
    "baseUrl": "src",
    "paths": {
      "@/*": ["*"],
      "@/components/*": ["components/*"],
      "@/utils/*": ["utils/*"]
    }
  },
  "include": ["src"],
  "exclude": ["node_modules"]
}</code></pre>
        
        <h2>Error Boundaries with TypeScript</h2>
        <pre><code>import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; retry: () => void }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({
      error,
      errorInfo
    });

    // Log error to monitoring service
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Example: Send to error tracking service
    console.error('Error caught by boundary:', error, errorInfo);
  }

  retry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error!} retry={this.retry} />;
      }

      return (
        <div className="error-boundary">
          <h2>Something went wrong</h2>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            <summary>Error details</summary>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo.componentStack}
          </details>
          <button onClick={this.retry}>Try again</button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Usage
const App: React.FC = () => {
  return (
    <ErrorBoundary
      fallback={({ error, retry }) => (
        <div>
          <h1>Oops! Something went wrong</h1>
          <p>{error.message}</p>
          <button onClick={retry}>Retry</button>
        </div>
      )}
      onError={(error, errorInfo) => {
        // Send to error tracking service
        console.error('App error:', error, errorInfo);
      }}
    >
      <Router>
        <Routes>
          {/* Your routes */}
        </Routes>
      </Router>
    </ErrorBoundary>
  );
};</code></pre>
        
        <h2>Environment Configuration</h2>
        <pre><code>// src/config/environment.ts
interface EnvironmentConfig {
  apiUrl: string;
  appName: string;
  version: string;
  isDevelopment: boolean;
  isProduction: boolean;
  enableAnalytics: boolean;
}

const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = process.env.NODE_ENV || 'development';
  
  return {
    apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:3001',
    appName: process.env.REACT_APP_NAME || 'My App',
    version: process.env.REACT_APP_VERSION || '1.0.0',
    isDevelopment: env === 'development',
    isProduction: env === 'production',
    enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true'
  };
};

export const config = getEnvironmentConfig();

// Type-safe environment variables
declare global {
  namespace NodeJS {
    interface ProcessEnv {
      REACT_APP_API_URL: string;
      REACT_APP_NAME: string;
      REACT_APP_VERSION: string;
      REACT_APP_ENABLE_ANALYTICS: string;
    }
  }
}

// Usage in components
import { config } from '@/config/environment';

const ApiService = {
  baseUrl: config.apiUrl,
  
  async fetchUser(id: number): Promise<User> {
    const response = await fetch(\`\${this.baseUrl}/users/\${id}\`);
    return response.json();
  }
};</code></pre>
        
        <h2>Performance Optimization</h2>
        <pre><code>import React, { lazy, Suspense, memo, useMemo, useCallback } from 'react';

// Lazy loading with TypeScript
const Dashboard = lazy(() => import('./components/Dashboard'));
const Profile = lazy(() => import('./components/Profile'));

// Memoized component
interface UserCardProps {
  user: User;
  onEdit: (user: User) => void;
}

const UserCard = memo<UserCardProps>(({ user, onEdit }) => {
  const handleEdit = useCallback(() => {
    onEdit(user);
  }, [user, onEdit]);

  const formattedDate = useMemo(() => {
    return new Date(user.createdAt).toLocaleDateString();
  }, [user.createdAt]);

  return (
    <div className="user-card">
      <h3>{user.name}</h3>
      <p>Joined: {formattedDate}</p>
      <button onClick={handleEdit}>Edit</button>
    </div>
  );
});

UserCard.displayName = 'UserCard';

// Optimized list rendering
interface UserListProps {
  users: User[];
  onEditUser: (user: User) => void;
}

const UserList: React.FC<UserListProps> = ({ users, onEditUser }) => {
  const memoizedUsers = useMemo(() => users, [users]);

  return (
    <div className="user-list">
      {memoizedUsers.map(user => (
        <UserCard
          key={user.id}
          user={user}
          onEdit={onEditUser}
        />
      ))}
    </div>
  );
};

// App with lazy loading
const App: React.FC = () => {
  return (
    <Router>
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile" element={<Profile />} />
        </Routes>
      </Suspense>
    </Router>
  );
};</code></pre>
      `,
      practice: {
        task: "Create a production-ready TypeScript React app structure with error handling and performance optimization",
        starter: `// Set up production configuration and error handling`,
        solution: `// src/types/global.d.ts
export interface User {
  id: number;
  name: string;
  email: string;
  createdAt: string;
}

export interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

// src/utils/errorTracking.ts
interface ErrorTrackingService {
  captureException: (error: Error, context?: Record<string, any>) => void;
  captureMessage: (message: string, level?: 'info' | 'warning' | 'error') => void;
}

class ErrorTracker implements ErrorTrackingService {
  captureException(error: Error, context?: Record<string, any>): void {
    console.error('Error tracked:', error, context);
    // In production, send to service like Sentry
  }

  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
    console.log(\`[\${level.toUpperCase()}] \${message}\`);
    // In production, send to logging service
  }
}

export const errorTracker = new ErrorTracker();

// src/components/LoadingSpinner.tsx
import React from 'react';

interface LoadingSpinnerProps {
  size?: 'small' | 'medium' | 'large';
  text?: string;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'medium', 
  text = 'Loading...' 
}) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-8 h-8',
    large: 'w-12 h-12'
  };

  return (
    <div className="flex flex-col items-center justify-center p-4">
      <div className={\`animate-spin rounded-full border-b-2 border-blue-600 \${sizeClasses[size]}\`} />
      <p className="mt-2 text-sm text-gray-600">{text}</p>
    </div>
  );
};

export default LoadingSpinner;

// src/App.tsx
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import { errorTracker } from './utils/errorTracking';

// Lazy loaded components
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Users = React.lazy(() => import('./pages/Users'));

const App: React.FC = () => {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        errorTracker.captureException(error, { errorInfo });
      }}
    >
      <Router>
        <div className="min-h-screen bg-gray-50">
          <header className="bg-white shadow">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h1 className="text-2xl font-bold text-gray-900 py-4">
                Production React App
              </h1>
            </div>
          </header>
          
          <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <Suspense fallback={<LoadingSpinner size="large" text="Loading page..." />}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/users" element={<Users />} />
              </Routes>
            </Suspense>
          </main>
        </div>
      </Router>
    </ErrorBoundary>
  );
};

export default App;`,
        hints: [
          "Create proper TypeScript interfaces for all data structures",
          "Implement comprehensive error tracking and logging",
          "Use lazy loading for better performance",
          "Set up proper loading states and error boundaries"
        ]
      }
    }
  ],
  requirements: [
    "Solid understanding of React fundamentals",
    "Basic knowledge of JavaScript ES6+",
    "Familiarity with modern development tools",
    "Understanding of component-based architecture"
  ],
  tags: ["TypeScript", "React", "Type Safety", "Professional Development"],
  imageSrc: "/images/course-thumbnail.png",
  learningOutcomes: [
    "Write type-safe React applications with TypeScript",
    "Implement advanced TypeScript patterns in React",
    "Create reusable, type-safe components and hooks",
    "Test TypeScript React applications effectively",
    "Deploy and maintain production TypeScript React apps",
    "Debug and troubleshoot TypeScript compilation issues"
  ],
  certificateTemplate: "typescript-react-professional"
};
